import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import FetchPlaces from "./FetchPlaces";
import "./Cards.css";
import { Link } from "react-router-dom";
import SortingPanel from "./SortingPanel";
import settings, { saveSetting } from "./settings";
import CategoryTitle from "./CategoryTitle";
import { useLanguage } from "./LanguageContext";
import { placesQueryKey, usePlacesQuery } from "../queries/places";

function Cards(props) {
  const { t } = useLanguage();
  const [resultsOnPage, setResultsOnPage] = useState(settings.resultsOnPagePlaces);
  const [cardsSize, setCardsSize] = useState(settings.cardsSizePlaces);
  const [isSorted, setIsSorted] = useState(false);
  const queryClient = useQueryClient();

  // Search.js reuses Cards with an already-fetched `places` list (search results);
  // category routes leave `places` undefined and let Cards fetch (and cache) its own.
  const isExternalCollection = props.places !== undefined;
  const placesQuery = usePlacesQuery(props.category, { enabled: !isExternalCollection });

  const places = isExternalCollection ? props.places : (placesQuery.data ?? []);
  const setPlaces = isExternalCollection
    ? props.setPlaces
    : (next) => queryClient.setQueryData(placesQueryKey(props.category), next);
  const dataLoading = isExternalCollection ? props.loading : placesQuery.isLoading;

  // Only reset the sort flag on a genuine category change, not on every mount:
  // when data is already cached, SortingPanel can finish sorting (setIsSorted(true))
  // before this effect's own StrictMode replay runs, and an unconditional reset here
  // would clobber that and leave the page stuck on its loader.
  const previousCategoryRef = useRef(null);
  useEffect(() => {
    if (previousCategoryRef.current !== null && previousCategoryRef.current !== props.category) {
      setIsSorted(false);
    }
    previousCategoryRef.current = props.category;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [props.category]);

  function setResultsOnPageWithSave(value) {
    setResultsOnPage(value);
    saveSetting("resultsOnPagePlaces", value);
  }

  function handleCardsSizeClick() {
    const nextSize = !cardsSize;
    setCardsSize(nextSize);
    saveSetting("cardsSizePlaces", nextSize);
  }

  const hasPlaces = places?.length > 0;
  const isLoading = dataLoading || (hasPlaces && isSorted === false);

  return (
    <div>
      <CategoryTitle category={props.category} />

      {hasPlaces && dataLoading === false ? (
        <SortingPanel
          breadcrumbCategory={props.category}
          resultsOnPage={resultsOnPage}
          setResultsOnPage={setResultsOnPageWithSave}
          cardsSize={handleCardsSizeClick}
          collection={places}
          setCollection={setPlaces}
          setLoading={props.setLoading}
          target="place"
          setPageNumber={props.setPageNumber}
          isSorted={isSorted}
          setIsSorted={setIsSorted}
        />
      ) : null}

      {isLoading ? (
        <div className="loader"></div>
      ) : (
        <div className={cardsSize ? undefined : "card-deck card-deck-cards"}>
          <FetchPlaces
            places={places}
            cardsSize={cardsSize}
            resultsOnPage={resultsOnPage}
            pageNumber={props.pageNumber}
            setPageNumber={props.setPageNumber}
            search={props.search}
          />
        </div>
      )}

      {!hasPlaces && dataLoading === false ? (
        <div className="cards-bottom-text">
          <p>{t("empty")}</p>
          <p>{t("changeIt")}</p>
        </div>
      ) : null}

      <div className="cards-bottom-text">
        <Link to="/kontakt">{t("suggestPlace")}</Link>
      </div>
    </div>
  );
}

export default Cards;
